"use client";

import dynamic from "next/dynamic";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslations } from "next-intl";
import { Gift, Loader2, MinusCircle, PlusCircle } from "lucide-react";
import type { Value } from "react-phone-number-input";
import { AutoFallbackImage } from "@/components/ui/auto-fallback-image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/ui/phone-input";
import { DateTimePicker } from "@/components/ui/DateTimePicker";
import { ThemedActionButton } from "@/components/ui/themed-action-button";
import { CurrencyCodeDisplay } from "@/components/currency/CurrencyCodeDisplay";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { CartIcon } from "@/components/icons/CartIcon";
import { useThemeEditor } from "@/components/theme-editor";
import { useRouter } from "@/i18n/routing";
import { useCartStore } from "@/store/cart";
import { resolveImageUrl } from "@/lib/utils";
import { parseMultiLangField } from "@/lib/utils/i18n";
import {
  cleanPhoneNumber,
  getMobileDigitCount,
  MOBILE_MAX_LENGTH,
} from "@/lib/utils/phone";
import type { UnifiedDonationProjectParams } from "@/components/checkout/unifiedDonationTypes";
import type { AdvancedGiftCategory, GiftInfo, Project, ProjectShare } from "@/types";

const UnifiedDonationModal = dynamic(
  () =>
    import("@/components/checkout/UnifiedDonationModal").then((m) => ({
      default: m.UnifiedDonationModal,
    })),
  { ssr: false },
);

type AdvancedGiftDonationPageProps = {
  project: Project;
  categories: AdvancedGiftCategory[];
  locale: "ar" | "en";
  isRTL: boolean;
};

type GiftFormValues = {
  recipientName: string;
  recipientMobile: Value;
  senderName: string;
  sendSmsCopy: boolean;
  sendTiming: "now" | "later";
  sendTime?: string;
};

function localized(value: string | null | undefined, locale: "ar" | "en") {
  return parseMultiLangField(value ?? "", locale);
}

function amountValue(share: ProjectShare): number {
  return share.MinDonationAmount ?? share.ShareAmount ?? 0;
}

function amountPresets(share: ProjectShare): number[] {
  const presets = (share.AmountClasses ?? [])
    .map((item) => item.Amount)
    .filter((amount) => Number.isFinite(amount) && amount > 0);
  return presets.length
    ? presets
    : [amountValue(share)].filter((amount) => amount > 0);
}

const DIGITS_REGEX = /[0-9\u0660-\u0669\u06F0-\u06F9]/;
const inputClassName =
  "h-11 rounded-lg bg-white border-gray-200 text-sm placeholder:text-gray-400";

export function AdvancedGiftDonationPage({
  project,
  categories,
  locale,
  isRTL,
}: AdvancedGiftDonationPageProps) {
  const t = useTranslations("gifts.advanced");
  const tGifts = useTranslations("gifts");
  const tCommon = useTranslations("common");
  const tAuth = useTranslations("auth");
  const router = useRouter();
  const addItem = useCartStore((state) => state.addItem);
  const openCart = useCartStore((state) => state.openCart);
  const { config: themeConfig } = useThemeEditor();

  const [categoryId, setCategoryId] = useState<number | null>(
    categories[0]?.Id ?? null,
  );
  const selectedCategory = useMemo(
    () =>
      categories.find((category) => category.Id === categoryId) ??
      categories[0],
    [categories, categoryId],
  );
  const [shareId, setShareId] = useState<number | null>(
    selectedCategory?.Shares[0]?.Id ?? null,
  );
  const selectedShare = useMemo(() => {
    const shares = selectedCategory?.Shares ?? [];
    return shares.find((share) => share.Id === shareId) ?? shares[0] ?? null;
  }, [selectedCategory, shareId]);
  const presets = selectedShare ? amountPresets(selectedShare) : [];
  const [amount, setAmount] = useState<number>(presets[0] ?? 0);
  const [selectedPreset, setSelectedPreset] = useState<number | null>(
    presets[0] ?? null,
  );
  const [quantity, setQuantity] = useState(1);
  const [addingAction, setAddingAction] = useState<1 | 2 | null>(null);
  const [paymentParams, setPaymentParams] =
    useState<UnifiedDonationProjectParams | null>(null);

  const formatter = useMemo(
    () => new Intl.NumberFormat(locale === "ar" ? "ar-SA" : "en-US"),
    [locale],
  );

  const formSchema = useMemo(
    () =>
      z
        .object({
          recipientName: z
            .string()
            .min(1, tCommon("required", { defaultValue: "Required" }))
            .refine((val) => !DIGITS_REGEX.test(val), {
              message: tGifts("nameNoDigits"),
            }),
          recipientMobile: z
            .custom<Value>(
              (val) => typeof val === "string" && val.trim().length > 0,
              { message: tAuth("mobileRequired") },
            )
            .refine(
              (val) =>
                getMobileDigitCount(cleanPhoneNumber(val || "")) >= 9,
              { message: tAuth("invalidMobile") },
            )
            .refine(
              (val) => getMobileDigitCount(val || "") <= MOBILE_MAX_LENGTH,
              { message: tAuth("mobileMaxLength") },
            ),
          senderName: z
            .string()
            .min(1, tCommon("required", { defaultValue: "Required" }))
            .refine((val) => !DIGITS_REGEX.test(val), {
              message: tGifts("nameNoDigits"),
            }),
          sendSmsCopy: z.boolean(),
          sendTiming: z.enum(["now", "later"]),
          sendTime: z.string().optional(),
        })
        .refine(
          (data) =>
            data.sendTiming !== "later" ||
            (typeof data.sendTime === "string" &&
              data.sendTime.trim().length > 0),
          {
            message: tCommon("required", { defaultValue: "Required" }),
            path: ["sendTime"],
          },
        ),
    [tAuth, tCommon, tGifts],
  );

  const form = useForm<GiftFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      recipientName: "",
      recipientMobile: undefined,
      senderName: "",
      sendSmsCopy: true,
      sendTiming: "now",
      sendTime: "",
    },
    mode: "onTouched",
  });

  const sendTiming = form.watch("sendTiming");
  const minAmount = selectedShare ? amountValue(selectedShare) : 0;
  const amountValid = amount > 0 && amount >= minAmount;

  const handleCategoryChange = (nextCategory: AdvancedGiftCategory) => {
    const nextShare = nextCategory.Shares[0] ?? null;
    const nextPresets = nextShare ? amountPresets(nextShare) : [];
    const nextAmount = nextPresets[0] ?? 0;
    setCategoryId(nextCategory.Id);
    setShareId(nextShare?.Id ?? null);
    setAmount(nextAmount);
    setSelectedPreset(nextPresets[0] ?? null);
  };

  const handleShareChange = (share: ProjectShare) => {
    const nextPresets = amountPresets(share);
    setShareId(share.Id);
    setAmount(nextPresets[0] ?? amountValue(share));
    setSelectedPreset(nextPresets[0] ?? null);
  };

  const handlePresetClick = (preset: number) => {
    setSelectedPreset(preset);
    setAmount(preset);
  };

  const handleCustomAmountChange = (raw: string) => {
    const next = Number(raw) || 0;
    setAmount(next);
    setSelectedPreset(presets.includes(next) ? next : null);
  };

  const buildGiftInfo = (values: GiftFormValues): GiftInfo => ({
    recipientName: values.recipientName,
    recipientMobile: values.recipientMobile
      ? String(values.recipientMobile)
      : "",
    senderName: values.senderName,
    sendTiming: values.sendTiming,
    sendTime: values.sendTiming === "later" ? values.sendTime : undefined,
    sendSmsCopy: values.sendSmsCopy,
    message: "",
    attribute: "",
  });

  const buildPaymentParams = (
    giftInfo: GiftInfo,
  ): UnifiedDonationProjectParams => ({
    projectId: project.Id,
    associationId: project.AssociationId,
    projectName: project.Name,
    amount,
    quantity,
    shareId: selectedShare?.Id,
    projectImageUrl: resolveImageUrl({
      imageUrl: project.LogoUrl ?? null,
      imageId: project.LogoId,
    }),
    giftInfo,
  });

  const handleAddToCart = form.handleSubmit(async (values) => {
    if (!selectedShare || !amountValid) return;

    const giftInfo = buildGiftInfo(values);
    const typeSending = values.sendTiming === "later" ? 2 : 1;
    const messageDateTime =
      values.sendTiming === "later" ? values.sendTime : undefined;
    const useDrawer = themeConfig.cart.cartButtonAction === "drawer";

    setAddingAction(1);
    try {
      const result = await addItem(
        {
          ProjectId: project.Id,
          Amount: amount,
          Quantity: quantity,
          ProjectShareId: selectedShare.Id,
          DonateTypeId: project.DonateTypeId ?? null,
          DonationOpen: true,
          IsProduct: project.IsProduct ?? false,
          GiftProject: true,
          ProjectName: project.NameRaw ?? project.Name,
          ProjectImage: project.LogoUrl ?? project.LogoIdRaw ?? project.LogoId,
          giftInfo,
          sendToName: values.recipientName,
          sendToMobile: values.recipientMobile
            ? String(values.recipientMobile)
            : "",
          senderName: values.senderName,
          typeSending,
          wantCopyForYou: values.sendSmsCopy,
          messageDateTime,
        },
        { suppressFeedback: useDrawer },
      );

      if (result.success && useDrawer) {
        openCart();
      }
    } finally {
      setAddingAction(null);
    }
  });

  const handleGiftNow = form.handleSubmit(async (values) => {
    if (!selectedShare || !amountValid) return;

    const giftInfo = buildGiftInfo(values);
    setAddingAction(2);
    try {
      setPaymentParams(buildPaymentParams(giftInfo));
    } finally {
      setAddingAction(null);
    }
  });

  if (!categories.length || !selectedCategory || !selectedShare) {
    return (
      <div className="rounded-2xl border bg-white p-8 text-center text-muted-foreground">
        {t("empty")}
      </div>
    );
  }

  const previewImageUrl =
    selectedShare.ViewImageUrl ??
    selectedShare.ImageUrl ??
    project.LogoUrl ??
    null;
  const previewImageId =
    selectedShare.ViewImageId ??
    selectedShare.ImageId ??
    project.LogoId ??
    null;

  return (
    <div className="space-y-4 pb-8" dir={isRTL ? "rtl" : "ltr"}>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-start lg:gap-8">
        {/* Preview: sits on page gray background */}
        <aside className="order-2 lg:order-1 lg:col-span-4">
          <div className="relative mx-auto aspect-[3/4] w-full max-w-sm overflow-hidden rounded-2xl lg:max-w-none">
            <AutoFallbackImage
              imageUrl={previewImageUrl}
              imageId={previewImageId}
              alt={localized(selectedShare.ShareName, locale) || project.Name}
              fill
              className="object-contain"
              priority
            />
          </div>
        </aside>

        {/* Form: white card only */}
        <section className="order-1 space-y-5 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6 lg:order-2 lg:col-span-8">
          <Form {...form}>
            <form className="space-y-5" onSubmit={(e) => e.preventDefault()}>
              {/* Categories */}
              <div className="space-y-2.5">
                <p className="text-sm font-semibold text-foreground">
                  {t("chooseCategory")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {categories.map((category) => {
                    const selected = category.Id === selectedCategory.Id;
                    return (
                      <button
                        key={category.Id}
                        type="button"
                        onClick={() => handleCategoryChange(category)}
                        className={`h-10 rounded-lg px-4 text-sm font-medium transition ${
                          selected
                            ? "bg-primary text-primary-foreground shadow-sm"
                            : "border border-gray-200 bg-white text-foreground hover:bg-gray-50"
                        }`}
                      >
                        {localized(category.CategoryName, locale)}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Card models */}
              <div className="space-y-2.5">
                <p className="text-sm font-semibold text-foreground">
                  {t("chooseCard")}
                </p>
                <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-1">
                  {selectedCategory.Shares.map((share) => {
                    const selected = share.Id === selectedShare.Id;
                    return (
                      <button
                        key={share.Id}
                        type="button"
                        onClick={() => handleShareChange(share)}
                        className={`w-[7.5rem] shrink-0 overflow-hidden rounded-xl border bg-white p-1.5 text-center transition ${
                          selected
                            ? "border-primary ring-2 ring-primary/20"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="relative aspect-[4/3] overflow-hidden rounded-lg bg-muted">
                          <AutoFallbackImage
                            imageUrl={
                              share.ViewImageUrl ?? share.ImageUrl ?? null
                            }
                            imageId={share.ViewImageId ?? share.ImageId ?? null}
                            alt={localized(share.ShareName, locale)}
                            fill
                            className="object-contain"
                          />
                        </div>
                        <p className="mt-1.5 truncate px-0.5 text-xs font-medium text-foreground">
                          {localized(share.ShareName, locale)}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Names + mobile — one row on desktop */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="senderName"
                  render={({ field }) => (
                    <FormItem>
                      <Label
                        htmlFor="advanced-senderName"
                        className="mb-1.5 block text-sm font-semibold"
                      >
                        {t("senderNameLabel")}
                      </Label>
                      <FormControl>
                        <Input
                          id="advanced-senderName"
                          {...field}
                          placeholder={t("senderNamePlaceholder")}
                          className={inputClassName}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="recipientName"
                  render={({ field }) => (
                    <FormItem>
                      <Label
                        htmlFor="advanced-recipientName"
                        className="mb-1.5 block text-sm font-semibold"
                      >
                        {t("recipientNameLabel")}
                      </Label>
                      <FormControl>
                        <Input
                          id="advanced-recipientName"
                          {...field}
                          placeholder={t("recipientNamePlaceholder")}
                          className={inputClassName}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="recipientMobile"
                  render={({ field }) => (
                    <FormItem>
                      <Label
                        htmlFor="advanced-recipientMobile"
                        className="mb-1.5 block text-sm font-semibold"
                      >
                        {t("recipientMobileLabel")}
                      </Label>
                      <FormControl>
                        <PhoneInput
                          id="advanced-recipientMobile"
                          value={field.value}
                          onChange={field.onChange}
                          placeholder={tGifts("recipientMobilePlaceholder")}
                          defaultCountry="SA"
                          className={inputClassName}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Amount — full-row: presets | custom */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-foreground">
                    {t("chooseAmount")}
                  </p>
                  <div className="flex gap-2">
                    {presets.map((preset) => {
                      const selected = selectedPreset === preset;
                      return (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => handlePresetClick(preset)}
                          className={`inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded-lg text-sm font-semibold transition ${
                            selected
                              ? "bg-primary text-primary-foreground"
                              : "border border-gray-200 bg-white text-foreground hover:bg-gray-50"
                          }`}
                        >
                          <span>{formatter.format(preset)}</span>
                          <CurrencyCodeDisplay
                            code="SAR"
                            className="h-3.5 w-auto"
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-foreground">
                    {t("customAmount")}
                  </p>
                  <div className="flex h-11 w-full items-center gap-2 rounded-lg border border-gray-200 bg-white px-3">
                    <input
                      type="number"
                      inputMode="numeric"
                      min={minAmount}
                      value={selectedPreset == null ? amount || 0 : 0}
                      onChange={(e) =>
                        handleCustomAmountChange(e.target.value)
                      }
                      onFocus={() => {
                        if (selectedPreset != null) {
                          setSelectedPreset(null);
                        }
                      }}
                      className="min-w-0 flex-1 bg-transparent text-sm outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                    <CurrencyCodeDisplay
                      code="SAR"
                      className="h-3.5 w-auto shrink-0 text-gray-600"
                    />
                  </div>
                </div>
              </div>

              {/* Send timing */}
              <div className="space-y-2.5">
                <p className="text-sm font-semibold text-foreground">
                  {tGifts("sendTime")}
                </p>
                <FormField
                  control={form.control}
                  name="sendTiming"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 !py-0">
                          {(["now", "later"] as const).map((option) => {
                            const selected = field.value === option;
                            return (
                              <button
                                key={option}
                                type="button"
                                onClick={() => field.onChange(option)}
                                className={`flex h-11 items-center justify-center gap-2 rounded-lg border px-3 text-sm transition ${
                                  selected
                                    ? "border-gray-200 bg-[#f1f4f6] font-semibold text-foreground"
                                    : "border-gray-200 bg-white text-muted-foreground hover:bg-gray-50"
                                }`}
                              >
                                <span
                                  className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                                    selected
                                      ? "border-primary"
                                      : "border-gray-300"
                                  }`}
                                >
                                  {selected && (
                                    <span className="h-2 w-2 rounded-full bg-primary" />
                                  )}
                                </span>
                                {option === "now"
                                  ? tGifts("sendNow")
                                  : tGifts("sendLater")}
                              </button>
                            );
                          })}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {sendTiming === "later" && (
                  <FormField
                    control={form.control}
                    name="sendTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <DateTimePicker
                            value={field.value ?? ""}
                            onChange={field.onChange}
                            mode="datetime"
                            minDate={new Date(Date.now() + 3600000)}
                            staticCalendar
                          />
                        </FormControl>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {tGifts("minTimeMessage")}
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              {/* SMS copy */}
              <FormField
                control={form.control}
                name="sendSmsCopy"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2.5">
                      <FormControl>
                        <input
                          type="checkbox"
                          id="advanced-smsCopy"
                          className="h-4 w-4 rounded border-gray-300 accent-primary"
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      </FormControl>
                      <Label
                        htmlFor="advanced-smsCopy"
                        className="cursor-pointer text-sm font-normal text-foreground"
                      >
                        {tGifts("sendCopyToMobile")}
                      </Label>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Quantity — minus (orange) left / plus (green) right in visual terms */}
              <div
                role="group"
                aria-label="Quantity"
                className="flex items-center justify-between rounded-[10px] border border-gray-100 bg-[#f8f8f8] px-3 py-2"
              >
                <button
                  type="button"
                  onClick={() => setQuantity((q) => q + 1)}
                  className="p-0 border-0 bg-transparent cursor-pointer touch-manipulation"
                  aria-label="Increase quantity"
                >
                  <PlusCircle className="h-6 w-6 text-primary" />
                </button>
                <span className="min-w-10 text-center text-sm font-bold text-gray-800">
                  {quantity}
                </span>
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                  className="p-0 border-0 bg-transparent cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 touch-manipulation"
                  aria-label="Decrease quantity"
                >
                  <MinusCircle className="h-6 w-6 text-[#F6A21E]" />
                </button>
              </div>

              {/* Actions */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <ThemedActionButton
                  type="button"
                  className="h-12 rounded-xl"
                  disabled={!amountValid || addingAction !== null}
                  onClick={() => void handleGiftNow()}
                  icon={
                    addingAction === 2 ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Gift className="h-4 w-4" />
                    )
                  }
                >
                  {addingAction === 2
                    ? tCommon("loading", { defaultValue: "..." })
                    : t("giftNow")}
                </ThemedActionButton>
                <Button
                  type="button"
                  className="h-12 rounded-xl text-sm font-bold text-white hover:opacity-90"
                  style={{ backgroundColor: "#F6A21E" }}
                  disabled={!amountValid || addingAction !== null}
                  onClick={() => void handleAddToCart()}
                >
                  {addingAction === 1 ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CartIcon className="size-4 md:size-5" />
                  )}
                  {addingAction === 1
                    ? tCommon("loading", { defaultValue: "..." })
                    : t("addToCart")}
                </Button>
              </div>
            </form>
          </Form>
        </section>
      </div>

      <div className="rounded-xl bg-[#eef0f2] px-4 py-3 text-center text-sm text-muted-foreground">
        {t("footerNote")}
      </div>

      <UnifiedDonationModal
        isOpen={paymentParams != null}
        onClose={() => setPaymentParams(null)}
        onSuccess={(operationCode) => {
          setPaymentParams(null);
          router.push(`/donation/success?operation=${operationCode}`);
        }}
        projectParams={paymentParams ?? undefined}
      />
    </div>
  );
}
